import { useCallback, useEffect, useRef } from 'react';

import { MarginPrice } from '@swyftx/margin/build/types';

import env from '@shared/config';
import { TimesEnum } from '@shared/enums';
import { UserStore } from '@shared/store';

import { useIntervalEffect } from '@react-hookz/web';
import { EventSource } from 'eventsource';
import { DateTime } from 'luxon';

import PriceWorker from './shared-worker?sharedworker';
import { useMarginPricesStore } from './useMarginPricesStore';
import { useSwyftxMargin } from '../useSwyftxMargin';

const usePriceWorker = () => {
  const priceWorker = useRef<SharedWorker>();
  const eventSource = useRef<EventSource>();
  const { swyftxMarginEnabled } = useSwyftxMargin();
  const { userProfile } = UserStore.useUserStore;

  useIntervalEffect(() => {
    if (!swyftxMarginEnabled) return;

    if (priceWorker.current?.port) {
      priceWorker.current.port.postMessage({ type: 'ping' });
    }
  }, TimesEnum.SECOND);

  const sendConfig = useCallback(() => {
    if (!priceWorker.current) return;

    priceWorker.current.port.postMessage({
      type: 'config',
      data: JSON.stringify({ TRADE_UPDATES_SERVICE_URL: env.TRADE_UPDATES_SERVICE_URL }),
    });
  }, []);

  const savePriceData = useCallback((data: MarginPrice[]) => {
    useMarginPricesStore.getState().setPrices(data);
  }, []);

  const initSharedWorker = useCallback(() => {
    if (priceWorker.current !== undefined) return;

    priceWorker.current = new PriceWorker({ name: 'swyftx-price-worker' });

    priceWorker.current.port.onmessage = (e) => {
      if (e.data.type === 'pong') return;

      if (e.data.type === 'disconnect') {
        priceWorker.current = undefined;
        initSharedWorker();
        return;
      }

      if (e.data.payload === 'Connected') return;

      if (e.data.timestamp && DateTime.fromMillis(e.data.timestamp) < DateTime.now().minus({ seconds: 1 })) return;

      savePriceData(JSON.parse(e.data.data) as MarginPrice[]);
    };

    priceWorker.current.port.start();

    sendConfig();
  }, [savePriceData, sendConfig]);

  const initEventSource = useCallback(() => {
    if (eventSource.current !== undefined) return;

    // Connect to the SSE endpoint
    eventSource.current = new EventSource(`${env.TRADE_UPDATES_SERVICE_URL}/stream/prices`);

    // Listen for messages from the server
    eventSource.current.onmessage = (event) => {
      savePriceData(JSON.parse(event.data) as MarginPrice[]);
    };

    eventSource.current.onerror = () => {
      eventSource.current?.close();
      eventSource.current = undefined; // Allow reconnection
    };
  }, [savePriceData]);

  const handleBeforeUnload = () => {
    if (priceWorker.current?.port) {
      priceWorker.current.port.postMessage({ type: 'disconnect' });
    }

    if (eventSource.current) {
      eventSource.current.close();
    }
  };

  useEffect(() => {
    try {
      if (!swyftxMarginEnabled || !userProfile) return;

      if (window.SharedWorker) {
        initSharedWorker();
      } else {
        initEventSource();
      }

      window.addEventListener('beforeunload', handleBeforeUnload);
    } catch (e) {
      console.error(e);
    }

    return () => {
      priceWorker.current?.port.close();
      priceWorker.current = undefined;

      eventSource.current?.close();
      eventSource.current = undefined;

      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [initEventSource, initSharedWorker, sendConfig, swyftxMarginEnabled, userProfile]);
};

export { usePriceWorker };
